'use client';

import { useState, type ComponentType } from 'react';
import dynamic from 'next/dynamic';
import type { YouTubeProps } from 'react-youtube';
import { FaYoutube, FaPlay } from 'react-icons/fa';

// Dynamiczny import react-youtube — chunk pobierany dopiero po kliknięciu "Odtwórz",
// nie przy pierwszym renderze strony kursu.
// Rzutowanie na ComponentType<YouTubeProps> omija niezgodność w .d.ts biblioteki
// (propTypes.loading zbyt ogólne względem właściwego typu propsa 'lazy'|'eager'),
// widoczną tylko przez ścisłą inferencję generyków next/dynamic.
const YouTube = dynamic(
  () => import('react-youtube').then((mod) => mod.default as unknown as ComponentType<YouTubeProps>),
  { ssr: false }
);

// Wyodrębnia ID filmu YouTube z różnych formatów URL
function getYoutubeIdSimple(url: string): string {
  if (!url) return '';
  try {
    if (url.match(/^[a-zA-Z0-9_-]{11}$/)) {
      return url;
    }
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^?&]+)/i
    );
    return match && match[1] ? match[1] : url;
  } catch {
    return url;
  }
}

/**
 * Klient-side osadzenie filmu YouTube (react-youtube wymaga przeglądarki).
 * Wydzielone z page.tsx, by strona kursu mogła być Server Component (treść w SSR HTML).
 */
export default function CourseVideo({ youtube }: { youtube: string }) {
  const [playing, setPlaying] = useState(false);
  const videoId = getYoutubeIdSimple(youtube);
  const isValidId = !!videoId && typeof videoId === 'string' && videoId.length >= 5;

  return (
    <div className="mb-10 mt-8 max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b border-gray-200 pb-2 flex items-center">
        <FaYoutube className="mr-3 text-red-600" />
        <span>Film prezentacyjny</span>
      </h2>
      <div className="w-full aspect-video rounded-xl overflow-hidden shadow-lg relative">
        {!isValidId ? (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg text-center">
            Nieprawidłowy format linku YouTube lub ID filmu.
          </div>
        ) : playing ? (
          <YouTube
            videoId={videoId}
            // youtube-nocookie.com: jedyny host YouTube dozwolony w CSP frame-src (RODO-friendly)
            opts={{ host: 'https://www.youtube-nocookie.com', height: '100%', width: '100%', playerVars: { autoplay: 1 } }}
            className="absolute top-0 left-0 w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="Odtwórz film"
            className="absolute top-0 left-0 w-full h-full group cursor-pointer"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
              alt=""
              width={480}
              height={360}
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/45 transition-colors">
              <span className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600 text-white group-hover:scale-110 transition-transform">
                <FaPlay className="ml-1 text-2xl" aria-hidden="true" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="mt-4 text-center">
        <a
          href={youtube.includes('http') ? youtube : `https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <FaYoutube className="mr-2" />
          Obejrzyj na YouTube
        </a>
      </div>
    </div>
  );
}
